import * as THREE from 'three'
import { collectAssemblyNodeIdentityLookupIds } from '../../assembly/utils/AssemblyNodePath'
import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import type { ImportedModelResult } from '../../imported-model/loaders/types'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import type { ModelAssetFile, ModelAssetNode } from '../assets/model-asset-types'

/**
 * 组合器可消费的节点包解析结果最小契约。
 */
export interface EmbeddedPuppyOccurrencePackageResult {
  /** 包文件所属项目节点 key。 */
  readonly nodeKey: string
  /** 已解析的包文件。 */
  readonly file: ModelAssetFile
  /** imported-model 加载结果。 */
  readonly result: ImportedModelResult
}

/**
 * 一个项目 occurrence 对包内真实组件子树的精确认领结果。
 */
export interface EmbeddedPuppyOccurrenceClaim {
  /** 拥有容器包的项目模型节点，用于解析包内后代的唯一挂载作用域。 */
  readonly containerNode: ModelAssetNode
  /** 承载该 occurrence 的容器包结果。 */
  readonly packageResult: EmbeddedPuppyOccurrencePackageResult
  /** 包内组件的原始 hierarchy 根。 */
  readonly sourceHierarchy: GlbHierarchyNode
  /** 与 hierarchy 根 objectId 对应的真实场景对象。 */
  readonly sourceObject: THREE.Object3D
}

/**
 * 按前序收集项目模型节点。
 *
 * @param node 当前项目节点。
 * @returns 当前节点及其全部后代。
 */
function collectProjectNodesPreorder(node: ModelAssetNode): readonly ModelAssetNode[] {
  return [node, ...node.children.flatMap(collectProjectNodesPreorder)]
}

/**
 * 获取 imported-model 结果的场景根。
 *
 * @param result 已解析的模型结果。
 * @returns 可以建立 objectId 索引的场景对象。
 */
function resolveImportedResultRootObject(result: ImportedModelResult): THREE.Object3D {
  return result.kind === 'composed-assembly' ? result.root : result.object
}

/**
 * 建立包场景对象的 UUID 索引。
 *
 * @param root 包场景根。
 * @returns object.uuid 到 Object3D 的只读映射。
 */
function createObjectIndex(root: THREE.Object3D): ReadonlyMap<string, THREE.Object3D> {
  const objectByUuid = new Map<string, THREE.Object3D>()
  root.traverse((object) => {
    objectByUuid.set(object.uuid, object)
  })
  return objectByUuid
}

/**
 * 按前序收集包 hierarchy 节点。
 *
 * @param node 当前 hierarchy 节点。
 * @returns 当前节点及其全部后代。
 */
function collectHierarchyNodesPreorder(node: GlbHierarchyNode): readonly GlbHierarchyNode[] {
  return [node, ...node.children.flatMap(collectHierarchyNodesPreorder)]
}

/**
 * 判断 hierarchy 节点是否是可独立认领的组件根。
 *
 * @param node 包内 hierarchy 节点。
 * @returns assembly/part 组件且不是 Body 时返回 true。
 */
function isPackageComponentHierarchyNode(node: GlbHierarchyNode): boolean {
  const semanticKind = node.meta?.semanticKind
  return node.nodeType !== 'Body' && (semanticKind === 'assembly' || semanticKind === 'part')
}

/**
 * 判断解析结果是否来自指定的容器文件。
 *
 * @param resultFile 解析结果携带的文件身份。
 * @param containerFile occurrence 保存的容器文件身份。
 * @returns 对象相同、UUID 相同或 fileId 相同时返回 true。
 */
function isSameContainerFile(
  resultFile: ModelAssetFile,
  containerFile: ModelAssetFile,
): boolean {
  return resultFile === containerFile
    || Boolean(resultFile.uuid && containerFile.uuid && resultFile.uuid === containerFile.uuid)
    || resultFile.fileId === containerFile.fileId
}

/**
 * 判断两个装配节点身份集合是否存在精确归一化交集。
 *
 * @param left 左侧身份集合。
 * @param right 右侧身份集合。
 * @returns 存在相同归一化 token 时返回 true。
 */
function hasIdentityIntersection(
  left: readonly string[],
  right: ReadonlySet<string>,
): boolean {
  return left.some((identity) => right.has(identity))
}

/**
 * 读取 hierarchy 与 Object3D metadata 显式声明的包内 assemblyNodeId。
 *
 * 这里只读取身份字段，不使用名称、路径或模糊后缀，保证 project-model.json 的显式绑定
 * 不会在同名多实例场景中认领错误对象。
 *
 * @param hierarchy 包内组件 hierarchy。
 * @param object hierarchy 对应的场景对象。
 * @returns 归一化并去重的显式装配节点身份。
 */
function collectExplicitPackageAssemblyNodeIds(
  hierarchy: GlbHierarchyNode,
  object: THREE.Object3D,
): readonly string[] {
  const metadata = ImportedModelObjectMetadataService.peekOwnMetadata(object)
  return [
    hierarchy.meta?.assemblyNodeId,
    metadata?.assemblyNodeId,
    metadata?.selectionTarget?.assemblyNodeId,
    metadata?.hierarchy?.meta?.assemblyNodeId,
    object.userData?.puppy?.assemblyNodeId,
    object.userData?.assemblyNodeId,
  ]
    .flatMap(collectAssemblyNodeIdentityLookupIds)
    .filter((identity, index, identities) => identities.indexOf(identity) === index)
}

/**
 * 为全部 embedded puppy occurrence 建立唯一子树认领索引。
 *
 * 每个 occurrence 必须同时唯一命中容器包结果、组件 hierarchy 和真实 Object3D。
 * 任一环节缺失或歧义都会立即抛出明确错误，避免组合器继续构造身份污染的场景树。
 *
 * @param input 项目模型根与已解析节点包结果。
 * @returns 以 ModelAssetNode 对象身份索引的子树认领映射。
 */
export function createEmbeddedPuppyOccurrenceClaims(input: {
  readonly root: ModelAssetNode
  readonly packageResults: readonly EmbeddedPuppyOccurrencePackageResult[]
}): ReadonlyMap<ModelAssetNode, EmbeddedPuppyOccurrenceClaim> {
  const claims = new Map<ModelAssetNode, EmbeddedPuppyOccurrenceClaim>()
  const projectNodes = collectProjectNodesPreorder(input.root)
  const projectNodesByNodeKey = new Map<string, ModelAssetNode[]>()
  projectNodes.forEach((projectNode) => {
    const sameKeyNodes = projectNodesByNodeKey.get(projectNode.nodeKey) ?? []
    sameKeyNodes.push(projectNode)
    projectNodesByNodeKey.set(projectNode.nodeKey, sameKeyNodes)
  })

  for (const node of projectNodes) {
    const assetSource = node.assetSource
    if (assetSource?.kind !== 'embedded-puppy-occurrence') {
      continue
    }

    const packageResults = input.packageResults.filter((packageResult) => (
      packageResult.nodeKey === assetSource.containerNodeKey
      && isSameContainerFile(packageResult.file, assetSource.containerFile)
    ))
    if (packageResults.length !== 1) {
      throw new Error(
        `PuppyCAD 项目 occurrence 容器包无法唯一定位：nodeKey=${node.nodeKey}, containerNodeKey=${assetSource.containerNodeKey}, candidates=${packageResults.length}`,
      )
    }

    const containerNodes = projectNodesByNodeKey.get(assetSource.containerNodeKey) ?? []
    if (containerNodes.length !== 1) {
      throw new Error(
        `PuppyCAD 项目 occurrence 容器节点无法唯一定位：nodeKey=${node.nodeKey}, containerNodeKey=${assetSource.containerNodeKey}, candidates=${containerNodes.length}`,
      )
    }
    const containerNode = containerNodes[0]
    if (!containerNode) {
      throw new Error(
        `PuppyCAD 项目 occurrence 容器节点解析失败：nodeKey=${node.nodeKey}, containerNodeKey=${assetSource.containerNodeKey}`,
      )
    }

    const packageResult = packageResults[0]!
    const packageRoot = resolveImportedResultRootObject(packageResult.result)
    const objectByUuid = createObjectIndex(packageRoot)
    const targetAssemblyNodeIds = new Set(
      collectAssemblyNodeIdentityLookupIds(assetSource.assemblyNodeId),
    )
    const hierarchyCandidates = collectHierarchyNodesPreorder(packageResult.result.hierarchy)
      .filter(isPackageComponentHierarchyNode)
      .filter((hierarchy) => {
        const object = objectByUuid.get(hierarchy.objectId)
        return Boolean(object) && hasIdentityIntersection(
          collectExplicitPackageAssemblyNodeIds(hierarchy, object!),
          targetAssemblyNodeIds,
        )
      })

    if (hierarchyCandidates.length !== 1) {
      throw new Error(
        `PuppyCAD 项目 occurrence 包内组件无法唯一定位：nodeKey=${node.nodeKey}, assemblyNodeId=${assetSource.assemblyNodeId}, candidates=${hierarchyCandidates.length}`,
      )
    }

    const sourceHierarchy = hierarchyCandidates[0]!
    const sourceObject = objectByUuid.get(sourceHierarchy.objectId)
    if (!sourceObject) {
      throw new Error(
        `PuppyCAD 项目 occurrence 包内对象缺失：nodeKey=${node.nodeKey}, objectId=${sourceHierarchy.objectId}`,
      )
    }
    claims.set(node, {
      containerNode,
      packageResult,
      sourceHierarchy,
      sourceObject,
    })
  }

  return claims
}
